import { useState, useEffect, useCallback, useRef } from 'react';

type ScrollDirection = 'up' | 'down' | 'top';

interface UseHideOnScrollOptions {
  threshold?: number;
  elementId?: string;
}

/**
 * Hook para controlar visibilidade do header baseado na direção do scroll.
 * Comportamento iOS nativo: header esconde ao scrollar para baixo,
 * reaparece ao scrollar para cima (pull-to-reveal pattern).
 */
export const useHideOnScroll = (options: UseHideOnScrollOptions = {}) => {
  const { threshold = 10, elementId = 'app-main-content' } = options;
  
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('top');
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const updateScrollDirection = useCallback(() => {
    const element = document.getElementById(elementId);
    if (!element) {
      ticking.current = false;
      return;
    }

    const scrollY = element.scrollTop;
    const diff = scrollY - lastScrollY.current;

    // No topo da página - sempre visível
    if (scrollY < threshold) {
      setScrollDirection('top');
      setIsHeaderVisible(true);
    } 
    // Scrollando para baixo - esconder header
    else if (diff > threshold && scrollY > threshold) {
      setScrollDirection('down');
      setIsHeaderVisible(false);
    } 
    // Scrollando para cima - mostrar header (pull-to-reveal)
    else if (diff < -threshold) {
      setScrollDirection('up');
      setIsHeaderVisible(true);
    }

    lastScrollY.current = scrollY;
    ticking.current = false;
  }, [threshold, elementId]);

  useEffect(() => {
    const element = document.getElementById(elementId);
    if (!element) return;

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollDirection);
        ticking.current = true;
      }
    };

    element.addEventListener('scroll', onScroll, { passive: true });
    
    return () => {
      element.removeEventListener('scroll', onScroll);
    };
  }, [updateScrollDirection, elementId]);

  return { 
    isHeaderVisible, 
    scrollDirection,
    scrollY: lastScrollY.current 
  };
};
